import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  FiShoppingCart, FiUser, FiLogOut, FiClipboard,
  FiMapPin, FiHeart, FiLogIn, FiUserPlus, FiChevronDown,
  FiSearch, FiX,
} from "react-icons/fi";
import { useCart, useCustomer } from "../context/ShopContext";
import "./ShopTopBar.css";

function PortalDropdown({ triggerRef, isOpen, onClose, children }) {
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const recalc = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top:   rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };
    recalc();
    window.addEventListener("scroll", recalc, true);
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("scroll", recalc, true);
      window.removeEventListener("resize", recalc);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 99998, background: "transparent" }}
        onClick={onClose}
      />
      <div
        className="shop-topbar__dropdown"
        style={{ position: "fixed", top: coords.top, right: coords.right, left: "auto", zIndex: 99999 }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

/* ══════════════════════════════════════
   ShopTopBar
   searchProps: { value, onChange, onClear }  — optional
   When provided, renders a compact inline search input.
   ══════════════════════════════════════ */
export default function ShopTopBar({ searchProps }) {
  const { cartCount }        = useCart();
  const { customer, logout } = useCustomer();
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const btnRef      = useRef(null);
  const searchRef   = useRef(null);   // wrapper div ref
  const inputRef    = useRef(null);   // <input> ref
  const navigate    = useNavigate();

  /* Close dropdown on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") { setMenuOpen(false); setSearchExpanded(false); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  /* Collapse search when clicking outside the search wrapper */
  useEffect(() => {
    if (!searchExpanded) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [searchExpanded]);

  const handleLogout = () => { logout(); setMenuOpen(false); navigate("/shop"); };
  const closeMenu    = () => setMenuOpen(false);

  /* Tap the search icon / wrapper on mobile → expand & focus */
  const handleSearchWrapperClick = () => {
    if (!searchExpanded) {
      setSearchExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  };

  return (
    <div className="shop-topbar">
      <div className="container shop-topbar__inner">

        {/* "Back to site" is fully removed — no element rendered at all */}

        <div className="shop-topbar__actions">

          {/* ── Account / Auth ── */}
          {customer ? (
            <div className="shop-topbar__account">
              <button
                ref={btnRef}
                className="shop-topbar__account-btn"
                onClick={() => setMenuOpen(v => !v)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <FiUser size={16} />
                <span className="shop-topbar__account-name">{customer.first_name}</span>
                <FiChevronDown
                  size={13}
                  style={{ transition: "transform .2s", transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>
              <PortalDropdown triggerRef={btnRef} isOpen={menuOpen} onClose={closeMenu}>
                <Link to="/account/orders"   className="shop-topbar__dd-item" onClick={closeMenu}><FiClipboard size={14} /> Order History</Link>
                <Link to="/account/address"  className="shop-topbar__dd-item" onClick={closeMenu}><FiMapPin    size={14} /> Address Book</Link>
                <Link to="/account/wishlist" className="shop-topbar__dd-item" onClick={closeMenu}><FiHeart     size={14} /> Wishlist</Link>
                <Link to="/account/profile"  className="shop-topbar__dd-item" onClick={closeMenu}><FiUser      size={14} /> My Profile</Link>
                <div className="shop-topbar__dd-divider" />
                <button className="shop-topbar__dd-item shop-topbar__dd-item--danger" onClick={handleLogout}>
                  <FiLogOut size={14} /> Sign Out
                </button>
              </PortalDropdown>
            </div>
          ) : (
            <div className="shop-topbar__auth">
              <Link to="/login"    className="shop-topbar__auth-link"><FiLogIn   size={15} /> <span>Login</span></Link>
              <Link to="/register" className="shop-topbar__auth-link shop-topbar__auth-link--primary"><FiUserPlus size={15} /> <span>Register</span></Link>
            </div>
          )}

          {/* ── Inline Search (only when searchProps supplied) ── */}
          {searchProps && (
            <div
              ref={searchRef}
              className={`shop-topbar__search-wrap${searchExpanded ? " shop-topbar__search-wrap--expanded" : ""}`}
              onClick={handleSearchWrapperClick}
              role="search"
            >
              <FiSearch size={15} className="shop-topbar__search-icon" aria-hidden="true" />
              <input
                ref={inputRef}
                className="shop-topbar__search-input"
                type="text"
                placeholder="Search parts…"
                value={searchProps.value}
                onChange={searchProps.onChange}
                aria-label="Search products"
              />
              {searchProps.value && (
                <button
                  className="shop-topbar__search-clear"
                  onClick={(e) => { e.stopPropagation(); searchProps.onClear(); }}
                  aria-label="Clear search"
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          )}

          {/* ── Cart ── */}
          <Link to="/cart" className="shop-topbar__cart">
            <FiShoppingCart size={18} />
            {cartCount > 0 && <span className="shop-topbar__cart-badge">{cartCount}</span>}
            <span className="shop-topbar__cart-label">Cart</span>
          </Link>

        </div>
      </div>
    </div>
  );
}